import { useMemo } from "react"
import { GanttChart, type GanttRow } from "./gantt-chart"
import { formatCellValue } from "@/lib/utils"
import type { EngagementFinanceRow } from "@/types/report"
import { format } from "date-fns"

function statusToColor(status: string | null): string {
  switch (status) {
    case "Active": return "bg-emerald-500"
    case "Completed": return "bg-blue-500"
    case "PendingChange": return "bg-orange-500"
    default: return "bg-slate-400"
  }
}

function EngagementTooltip({ row }: { row: EngagementFinanceRow }) {
  return (
    <div className="space-y-1">
      <div className="font-medium">{row.engagementName}</div>
      <div className="text-muted-foreground">{row.engagementCode}</div>
      {row.client && <div className="text-muted-foreground">Client: {row.client}</div>}
      {row.billingType && <div className="text-muted-foreground">Billing: {row.billingType}</div>}
      {row.startDate && row.endDate && (
        <div className="text-muted-foreground">
          {format(new Date(row.startDate), "MMM d, yyyy")} — {format(new Date(row.endDate), "MMM d, yyyy")}
        </div>
      )}
      {row.contractAmount > 0 && (
        <div className="text-muted-foreground">
          {/* Through the grid's own formatter, so the tooltip and the cell behind it cannot
              disagree about the same figure. */}
          Contract: {formatCellValue(row.contractAmount, "currency", row.currency ?? undefined)}
        </div>
      )}
      {row.engagementLead && <div className="text-muted-foreground">Lead: {row.engagementLead}</div>}
    </div>
  )
}

interface EngagementGanttProps {
  data: EngagementFinanceRow[]
}

export function EngagementGantt({ data }: EngagementGanttProps) {
  const rows = useMemo<GanttRow[]>(() => {
    return data
      .filter(row => row.startDate && row.endDate)
      .map(row => ({
        id: row.id,
        label: row.engagementName ?? row.engagementCode ?? "—",
        sublabel: row.client ?? undefined,
        startDate: new Date(row.startDate!),
        endDate: new Date(row.endDate!),
        color: statusToColor(row.engagementStatus),
        tooltip: <EngagementTooltip row={row} />,
      }))
  }, [data])

  return <GanttChart rows={rows} />
}
